import { buildExcelBlob, type BuildExcelOptions, type WorkbookItem } from './excel-workbook';
import { isLowQualityPhoto } from './findings';
import { receiptFilenameStem, uniqueSheetName, uniqueStem } from './filename';
import type { ReceiptSession } from './types';

/**
 * v1.3 batch ZIP export.
 *
 * Bundles every successfully-processed receipt in the current batch as:
 *   - one `vendor_Mon_YY.jpg` per receipt (the post-scan-correct image,
 *     or the pass-through original if no correction was needed — both are
 *     re-encoded JPEG by the worker so the ZIP is uniformly JPEG)
 *   - one `receipts.xlsx` workbook (Summary sheet + per-receipt breakdown
 *     sheets — same shape as the standalone xlsx export, see
 *     `excel-workbook.ts`)
 *
 * Skips sessions that didn't parse to a receipt or that don't have a
 * corrected-image blob attached. The ZIP is built fully in-memory; jszip
 * is lazy-imported so it doesn't bloat the initial chunk per CLAUDE.md
 * convention #3.
 */

interface ExportItem extends WorkbookItem {
  session: ReceiptSession;
  /** Filename inside the ZIP, including extension. Unique within the batch. */
  jpgName: string;
}

function planExports(sessions: readonly ReceiptSession[]): ExportItem[] {
  // Two collision maps: filename stems and sheet stems. Sheet names are
  // capped at 31 chars by Excel — uniqueSheetName truncates leaving room
  // for a dedupe suffix, then dedupes.
  const fileSeen = new Map<string, number>();
  const sheetSeen = new Map<string, number>();
  const items: ExportItem[] = [];

  for (const session of sessions) {
    if (session.state.kind !== 'success' || session.receipt === null) continue;
    if (session.correctedImage === null) continue;

    const r = session.receipt;
    const stem = receiptFilenameStem(r.vendor.value, r.date.value);
    const jpgName = `${uniqueStem(stem, fileSeen)}.jpg`;
    const sheetName = uniqueSheetName(stem, sheetSeen);

    items.push({
      session,
      receipt: r,
      // Summary sheet's "Filename" column points to the JPG inside the
      // ZIP so a user opening the workbook can locate the matching image
      // without guessing.
      displayName: jpgName,
      jpgName,
      sheetName,
      lowQuality: isLowQualityPhoto(session),
    });
  }

  return items;
}

export async function exportZipBatch(
  sessions: readonly ReceiptSession[],
  opts: BuildExcelOptions = {},
): Promise<Blob | null> {
  const items = planExports(sessions);
  if (items.length === 0) return null;

  const [{ default: JSZip }, xlsxBlob] = await Promise.all([
    import('jszip'),
    buildExcelBlob(items, opts),
  ]);

  const zip = new JSZip();
  zip.file('receipts.xlsx', xlsxBlob);
  for (const it of items) {
    zip.file(it.jpgName, it.session.correctedImage!.blob);
  }

  // STORE compression — JPEGs and XLSX (already zip-compressed internally)
  // don't shrink meaningfully under DEFLATE, so we save the CPU.
  return zip.generateAsync({ type: 'blob', compression: 'STORE' });
}
